import React, {useEffect, useState, useMemo, useContext} from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { omit } from 'lodash';
import { Spin } from '@1c/ui';
import { useNavigate } from 'src/utils/useNavigate';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import Button from 'src/components/Button';
import InputField from 'src/components/InputField';
import TextAreaField from 'src/components/TextAreaField';
import SelectField from 'src/components/SelectField';
import IntegerField from 'src/components/IntegerField';
import PhoneInput from 'src/components/PhoneInput';
import routes from 'src/constants/routes';
import { postApplicationAPI } from 'src/serverApi/applications';
import useApplicationFormData from 'src/dataHooks/useApplicationFormData';
import { AuthorizationContext } from 'src/contexts/authorizationContext';
import validate from './validate';
import styles from './styles.module.scss';

const INN_MASK_PROPS = { integerLimit: 12, allowLeadingZeroes: true };

type Props = {
  allowedBanks: any,
}

const Form: React.FC<Props> = ({ allowedBanks }) => {
  const navigate = useNavigate();

  const { bankUeid } = useParams();
  const { fetchFormData, isFormDataLoading, formData } = useApplicationFormData();
  const authorizationState = useContext(AuthorizationContext);

  const bank = useMemo(() => {
    return (allowedBanks?.partnerBanks || []).find((bank: any) => bank.ueid === bankUeid);
  }, [allowedBanks]);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchFormData(bankUeid);
  }, []);

  const handleSubmit = async (values: any) => {
    const data = {
      ...omit(values, 'region'),
      bankUeid: bank?.ueid,
      regionId: values.region?.id,
    };

    try {
      setIsLoading(true);
      await postApplicationAPI(data);
      navigate(authorizationState?.userId ? routes.agent : routes.main);
      toast.success('Заявка в банк успешно отправлена');
    } catch (error) {
      toast.error('Ошибка отправки заявления');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(authorizationState?.userId ? routes.agent : routes.main);
  };

  if (isFormDataLoading) {
    return <Spin size={60} className={styles['spin']} />;
  }

  return (
    <FinalForm
      onSubmit={handleSubmit}
      submitting={isLoading}
      pristine
      validate={validate}
      initialValues={{}}
    >
      {({ submitting, pristine }: { submitting: any; pristine: any }) => {
        return (
          <div>
            <InputField label="Банк" value={bank?.name} disabled className={styles['input']} />
            <FinalField name="inn">
              {({ value, onChange, error }: { value: any; onChange: any; error: string }) => (
                <IntegerField
                  label="ИНН"
                  value={value}
                  onChange={onChange}
                  className={styles['input']}
                  maskProps={INN_MASK_PROPS}
                  error={error}
                />
              )}
            </FinalField>
            <FinalField name="region">
              {({ value, onChange, error }: { value: any; onChange: any; error: string }) => (
                <SelectField
                  value={value}
                  onChange={onChange}
                  options={formData?.regions}
                  getOptionValue={(option: any) => option.id}
                  getOptionLabel={(option: any) => option.name}
                  isSearchable
                  label="Регион"
                  placeholder="Выберите регион"
                  error={error}
                  className={styles['input']}
                />
              )}
            </FinalField>
            <FinalField name="phoneNumber">
              {({ value, onChange, error }: { value: any; onChange: any; error: string }) => (
                <PhoneInput
                  label="Телефон"
                  value={value}
                  onChange={onChange}
                  className={styles['input']}
                  error={error}
                />
              )}
            </FinalField>
            <FinalField name="email">
              {({ value, onChange, error }: { value: any; onChange: any; error: string }) => (
                <InputField
                  label="E-mail"
                  value={value}
                  onChange={onChange}
                  className={styles['input']}
                  error={error}
                />
              )}
            </FinalField>
            <FinalField name="comment">
              {({ value, onChange }: { value: any; onChange: any }) => (
                <TextAreaField
                  label="Комментарий для банка"
                  value={value}
                  onChange={onChange}
                  className={styles['input']}
                />
              )}
            </FinalField>
            <Button
              disabled={submitting || pristine}
              width="available"
              type="submit"
              className={styles['button']}
            >
              Отправить
            </Button>
            <Button
              variant="text"
              width="available"
              disabled={submitting}
              onClick={handleBack}
              className={styles['button']}
            >
              Назад
            </Button>
          </div>
        );
      }}
    </FinalForm>
  );
};

export default Form;
